import { useEffect, useState } from "react"

import { LocalStorageKeys, useLocalStorage } from "~/ui-rtk/utils/storage"
import useAccess from "~/ui-rtk/hooks/access"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"

// companyId → the sosReportReadyNotifiedAt value the user has already seen.
// Keyed per company so a fresh readiness stamp (job re-run) shows the popup
// once again, and one user's companies don't suppress each other.
type TSeenMap = Record<string, string>

export default function useSosReportReadyPopup() {
  const { isSosFree } = useAccess()
  const currentCompany = useCurrentCompany()
  const [seenMap, setSeenMap] = useLocalStorage<TSeenMap>(
    LocalStorageKeys.SOS_REPORT_READY_POPUP_SEEN,
    {},
  )
  // useLocalStorage is not reactive; kick a re-render on dismiss so the
  // popup hides immediately (same idiom as useMoneyWarnDialog).
  const [, rerender] = useState(0)

  const companyId = currentCompany?.id
  const notifiedAt = currentCompany?.metadata?.sosReportReadyNotifiedAt

  const isOpen = Boolean(
    isSosFree && companyId && notifiedAt && seenMap[companyId] !== notifiedAt,
  )

  // Latch "the popup showed this visit" so the coordinator can keep the
  // recurring upsell modal suppressed after dismissal — without this, the
  // modal would mount fresh (useState(true)) and auto-open back-to-back.
  const [wasOpenThisVisit, setWasOpenThisVisit] = useState(isOpen)
  useEffect(() => {
    if (isOpen) {
      setWasOpenThisVisit(true)
    }
  }, [isOpen])

  const handleClose = () => {
    if (companyId && notifiedAt) {
      setSeenMap({ ...seenMap, [companyId]: notifiedAt })
    }
    rerender(count => count + 1)
  }

  return { isOpen, wasOpenThisVisit, handleClose }
}
